import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { useCustomer } from '../customer-context.js';
import { formatOwners } from '../lib/owners.js';
import {
  Field,
  formatCurrency,
  formatDate,
  statusLabel,
  text,
  useCifAddress,
  useTermLoanDetail,
} from './account-detail-shared.js';

/**
 * SCRN 44 — Term Loan Information (CCF-110).
 *
 * Faithful port of the customer's Figma "Term loan information" design: a
 * single read-only, four-column horizontal box of the loan's overview fields.
 * The shared agent banner ("NavDark") lives in the app shell (AppLayout), so
 * this screen renders only the `<main>` content.
 *
 * The account number arrives as the `?account=` query param from the Dashboard
 * tile. Detail is fetched from the Teller-ID-gated BFF via the generated typed
 * client (`POST /api/termloan/details`, issue #329 — the dedicated R4
 * `termloan/details` op that carries the loan servicing fields).
 */
export function TermLoanInformationScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountNumber = searchParams.get('account') ?? '';
  const { detail, loading, error } = useTermLoanDetail(accountNumber);

  // "Address" field: the customer's **CIF address**, not the per-account Postal
  // Address (issue #638 — consistency with the Account Information screen, #603).
  const cifAddress = useCifAddress();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route and forwards the in-scope account context.
  const onProcessSelect = makeProcessMenuHandler(navigate, accountNumber);

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="term-loan-heading"
    >
      <div className="mb-4">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="term-loan-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-8"
        >
          Term loan information
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {loading && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
        >
          Loading account…
        </p>
      )}

      {detail && (
        <div className="border-2 border-[#dad7d3] p-6 mb-6 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 xl:divide-x xl:divide-[#e5e7eb]">
            <div className="xl:pr-6 flex flex-col gap-4">
              <Field label="IBAN" value={text(detail.Iban)} />
              {/* Account holder name(s), CCF-80 (issue #649): a joint account
                  lists ALL owners (comma-separated), from the `Owners` field
                  the BFF overlays from customer/accounts/list — consistent with
                  the Account Information (SCRN-45) overview. Falls back to the
                  single verified banner customer name. */}
              <Field
                label="Name"
                value={
                  formatOwners(
                    detail.Owners as string | string[] | undefined,
                  ) ||
                  customer.name ||
                  '—'
                }
              />
              <Field label="Address" value={text(cifAddress)} />
              <Field label="Account type" value="TL - Term loan" />
            </div>
            <div className="xl:px-6 flex flex-col gap-4">
              <Field
                label="Closing balance"
                value={formatCurrency(detail.Balance, detail.Currency)}
              />
              <Field
                label="Arrears"
                value={formatCurrency(detail.Arrears, detail.Currency)}
              />
              <Field
                label="Gross payment"
                value={formatCurrency(detail.GrossPayment, detail.Currency)}
              />
              <Field label="Interest rate" value={text(detail.InterestRate)} />
            </div>
            <div className="xl:px-6 flex flex-col gap-4">
              <Field label="Bill date" value={formatDate(detail.BillDate)} />
              <Field label="Frequency" value={text(detail.Frequency)} />
              <Field label="Term" value={text(detail.Term)} />
              <Field label="Payments made" value={text(detail.PaymentsMade)} />
            </div>
            <div className="xl:pl-6 flex flex-col gap-4">
              <Field
                label="Account status"
                value={statusLabel(detail.Status)}
              />
              <Field
                label="Deduct account number"
                value={text(detail.DeductAccountNumber)}
              />
              <Field
                label="Application date"
                value={formatDate(detail.ApplicationDate)}
              />
              <Field label="Open date" value={formatDate(detail.OpenedDate)} />
              <Field
                label="Drawn amount"
                value={formatCurrency(detail.DrawnAmount, detail.Currency)}
              />
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
